/**
 * The install command, and the live card that says whether it worked.
 *
 * **The command is shown with a placeholder, not with a token in it.** The design shows a
 * ready-to-paste command and captions it "your token is already filled in", and that is not
 * something this product can do: a token's value exists in exactly one HTTP response and only a
 * digest is kept afterwards. Athena cannot read your token back to you any more than it can read
 * your password back. So the command carries `YOUR_TOKEN` and the screen sends you to Tokens to
 * mint one, where the reveal dialog is the place to copy it from.
 *
 * This is also where a brand-new account lands, which the current design export decided and this
 * app follows: the first thing a new workspace needs is a connected assistant.
 *
 * **Two halves, one per step.** The MCP server answers questions; the skill is what makes the agent
 * ask them. The skill ships from the plugin repository, which is not in the Cursor Marketplace or a
 * public Claude Code marketplace, so step 2 is a terminal command that pulls `skills/athena` out of
 * that repository's tarball and writes it where the chosen client loads skills from. No `git`, no
 * clone left behind, and running it again is how it updates.
 *
 * The live card is `GET /api/v1/status`, polled. The server decides the state and returns it as one
 * field, because two clients combining booleans differently is how a status display starts lying.
 *
 * **Two cards, not three.** `StatusResponse.state` still has three values, and the export draws
 * two: waiting, and connected — which is what `answering` renders as here. The export's third card
 * is gone, and with it the line that read "2 assistants connected", a number nothing ever produced
 * (`call_count` counts calls, not callers). `StatusResponse.note` therefore has no reader in this
 * app; the state it names is still reached, it just does not get its own card.
 */

import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { CopyButton } from "../components/copy-button";
import { ErrorNotice, Loading } from "../components/feedback";
import * as workspaceApi from "../lib/api/workspace-api";
import { relativeTime } from "../lib/ui/format";
import { LIVE_POLL_MS, usePollTick } from "../lib/ui/use-poll";
import { useResource } from "../lib/ui/use-resource";

const TOKEN_PLACEHOLDER = "YOUR_TOKEN";

const PLUGIN_REPO = "hashaaamm/athena-plugin";
const PLUGIN_BRANCH = "main";

/**
 * Downloads the repository as a tarball and extracts only `skills/athena` into `dir`. The tarball's
 * top-level folder is `<repo>-<branch>`, which `--strip-components=2` removes together with
 * `skills/`, leaving `<dir>/athena/SKILL.md`. Works with both BSD tar (macOS) and GNU tar.
 */
function skillCommand(dir: string): string {
  const repoName = PLUGIN_REPO.split("/")[1];
  return `mkdir -p ${dir} && curl -fsSL https://codeload.github.com/${PLUGIN_REPO}/tar.gz/${PLUGIN_BRANCH} | tar -xz -C ${dir} --strip-components=2 ${repoName}-${PLUGIN_BRANCH}/skills/athena`;
}

const CLIENTS = [
  {
    id: "claude",
    label: "Claude Code",
    caption: "Claude Code · terminal",
    command: `claude mcp add --transport http athena ${mcpUrl()} --header "Authorization: Bearer ${TOKEN_PLACEHOLDER}"`,
    skillCaption: "Claude Code · .claude/skills/athena",
    skillCommand: skillCommand(".claude/skills"),
  },
  {
    id: "cursor",
    label: "Cursor",
    caption: "Cursor · ~/.cursor/mcp.json",
    command: `{
  "mcpServers": {
    "athena": {
      "url": "${mcpUrl()}",
      "headers": { "Authorization": "Bearer ${TOKEN_PLACEHOLDER}" }
    }
  }
}`,
    skillCaption: "Cursor · .agents/skills/athena",
    skillCommand: skillCommand(".agents/skills"),
  },
  {
    id: "codex",
    label: "Codex",
    caption: "Codex · ~/.codex/config.toml",
    command: `[mcp_servers.athena]
url = "${mcpUrl()}"
http_headers = { Authorization = "Bearer ${TOKEN_PLACEHOLDER}" }`,
    skillCaption: "Codex · .agents/skills/athena",
    skillCommand: skillCommand(".agents/skills"),
  },
  {
    id: "other",
    label: "Other",
    caption: "Any MCP client · streamable HTTP",
    command: `${mcpUrl()}
Authorization: Bearer ${TOKEN_PLACEHOLDER}`,
    skillCaption: "Agent Skills standard · .agents/skills/athena",
    skillCommand: skillCommand(".agents/skills"),
  },
] as const;

/**
 * The MCP endpoint, as an absolute URL somebody can paste into a config file on their laptop.
 *
 * **Configuration, not inference, and that is the whole point.** The first version of this derived
 * the origin from `VITE_API_BASE_URL`, which is the relative path `/api/v1` in development — so it
 * resolved against `location.origin` and printed the *dashboard's* hostname. The command looked
 * entirely plausible and pointed at a server with no `/mcp` on it. It was caught by reading the
 * command on the screen, not by a test, because every test that could have caught it would have
 * had to know the right answer already.
 *
 * So the deployed URL is supplied explicitly at build time. It is public by construction — it is
 * the address of a public endpoint — and it is emphatically not a credential.
 *
 * The fallbacks are ordered by how much they can be trusted:
 *   1. `VITE_MCP_URL`, which somebody set deliberately.
 *   2. The API base, when it is an absolute URL — then its origin is genuinely the API's.
 *   3. The current origin, which is correct only for a same-origin deployment.
 */
function mcpUrl(): string {
  const explicit = import.meta.env.VITE_MCP_URL;
  if (typeof explicit === "string" && explicit.length > 0) return explicit.replace(/\/$/, "");

  const base = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  try {
    if (/^https?:\/\//.test(base)) return `${new URL(base).origin}/mcp`;
    return new URL("/mcp", globalThis.location.origin).href;
  } catch {
    return "/mcp";
  }
}

export function InstallScreen() {
  const [client, setClient] = useState<(typeof CLIENTS)[number]["id"]>("claude");

  const load = useCallback((signal: AbortSignal) => workspaceApi.getStatus(signal), []);
  // Polls until an assistant has actually connected. After that the card is describing something
  // that already happened and there is nothing left to discover.
  const status = useResource(load, [usePollTick(LIVE_POLL_MS)]);

  const active = CLIENTS.find((entry) => entry.id === client) ?? CLIENTS[0];

  return (
    <>
      <h1 className="page-title">Install</h1>
      <p className="page-lede">Connect an assistant, then give it the skill that makes it ask.</p>

      <div className="tabs" role="tablist" aria-label="Client">
        {CLIENTS.map((entry) => (
          <button
            key={entry.id}
            className="tab"
            type="button"
            role="tab"
            aria-selected={client === entry.id}
            onClick={() => {
              setClient(entry.id);
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <h2 className="section-title">1. Connect the server</h2>
      <div className="command-card">
        <div className="command-card__head">
          <span className="command-card__caption">{active.caption}</span>
          <CopyButton value={active.command} className="command-card__copy" />
        </div>
        <pre className="command-card__body">{active.command}</pre>
      </div>

      <p className="page-footnote">
        Replace <span className="mono">{TOKEN_PLACEHOLDER}</span> with a token.{" "}
        <Link to="/tokens">Create one on the Tokens screen</Link> — its value is shown once, in the
        dialog that appears the moment it is created, and never again. Athena keeps only a
        fingerprint of it, so there is no way to pre-fill this command for you.
      </p>

      <h2 className="section-title">2. Add the skill</h2>
      <div className="command-card">
        <div className="command-card__head">
          <span className="command-card__caption">{active.skillCaption}</span>
          <CopyButton value={active.skillCommand} className="command-card__copy" />
        </div>
        <pre className="command-card__body">{active.skillCommand}</pre>
      </div>

      <p className="page-footnote">
        Run it in a terminal at the root of your repository. It downloads only the skill from{" "}
        <a href={`https://github.com/${PLUGIN_REPO}`} target="_blank" rel="noreferrer">
          github.com/{PLUGIN_REPO}
        </a>{" "}
        — Athena is not in a plugin marketplace yet — and needs no token. Commit the folder so
        everyone on the repository gets it, and run the command again to update it.
        {active.id === "other" &&
          " Most clients that support Agent Skills read .agents/skills; if yours reads skills from somewhere else, change the folder in the command."}
      </p>

      <h2 className="section-title">Is it working?</h2>
      {status.loading && <Loading what="your connection status" />}
      {status.error !== null && <ErrorNotice error={status.error} onRetry={status.reload} />}
      {status.data !== null && (
        <div className="status-card" data-state={status.data.state}>
          {status.data.state === "waiting" && (
            <>
              <p className="status-card__headline">
                Waiting. No assistant has connected with this workspace yet.
              </p>
              {/* The export's wording is "with this token". A token is not what this call is
                  scoped to — `GET /status` resolves a workspace from whichever credential asked,
                  so a second token connecting would clear this too. */}
              <p className="status-card__body">
                This updates by itself the moment one does — no refresh needed. Leave this open
                while you paste the command.
              </p>
            </>
          )}
          {status.data.state !== "waiting" && (
            <>
              <p className="status-card__headline">
                Connected. The Handbook has been searched {status.data.handbook_search_count}{" "}
                time{status.data.handbook_search_count === 1 ? "" : "s"}.
              </p>
              <p className="status-card__body">
                Next: publish something. The moment your assistant uses it, you will see it here.
              </p>
            </>
          )}
          <p className="status-card__meta">
            {status.data.call_count} call{status.data.call_count === 1 ? "" : "s"} ·{" "}
            {status.data.handbook_search_count} handbook search
            {status.data.handbook_search_count === 1 ? "" : "es"}
            {status.data.first_seen_at !== null && (
              <> · first seen {relativeTime(status.data.first_seen_at)}</>
            )}
          </p>
          <p className="status-card__lag">
            These counters can be up to {status.data.counter_lag_seconds} seconds behind. Athena
            does not name which assistant connected — it records that one did, and when.
          </p>
        </div>
      )}
    </>
  );
}
